<template>
  <div>
    <!-- Hero -->
    <section class="relative bg-hero-fade pt-20 pb-14 lg:pt-24">
      <div class="absolute inset-0 bg-grid-ink bg-grid-32 opacity-40 [mask-image:radial-gradient(ellipse_at_top,black_40%,transparent_70%)]"></div>
      <div class="relative container-wide max-w-4xl text-center">
        <div class="eyebrow mx-auto w-fit mb-4">Learn</div>
        <h1 class="font-display text-5xl lg:text-6xl text-ink-900 leading-[1.05] text-balance">
          SEO, <span class="italic text-brand-700">explained clearly.</span>
        </h1>
        <p class="mt-6 text-lg text-ink-600 max-w-2xl mx-auto text-pretty">
          Guides, glossary, and playbooks from our team and the community. Start anywhere.
        </p>
      </div>
    </section>

    <!-- Loading skeleton -->
    <section v-if="loading" class="container-wide max-w-6xl py-8">
      <div class="flex flex-wrap gap-2 justify-center mb-8">
        <div v-for="n in 6" :key="n" class="h-8 w-24 bg-ink-100 rounded-full animate-pulse"></div>
      </div>
      <div class="animate-pulse card overflow-hidden lg:grid lg:grid-cols-5 mb-6">
        <div class="lg:col-span-3 p-8 lg:p-12 space-y-3">
          <div class="h-3 w-24 bg-ink-100 rounded"></div>
          <div class="h-8 w-3/4 bg-ink-100 rounded"></div>
          <div class="h-4 w-full bg-ink-100 rounded"></div>
          <div class="h-4 w-2/3 bg-ink-100 rounded"></div>
        </div>
        <div class="lg:col-span-2 bg-ink-50 p-10"></div>
      </div>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div v-for="n in 6" :key="n" class="card p-6 space-y-3 animate-pulse">
          <div class="h-2 w-full bg-ink-100 rounded"></div>
          <div class="h-4 w-16 bg-ink-100 rounded"></div>
          <div class="h-5 w-full bg-ink-100 rounded"></div>
          <div class="h-3 w-full bg-ink-100 rounded"></div>
          <div class="h-3 w-2/3 bg-ink-100 rounded"></div>
        </div>
      </div>
    </section>

    <!-- Error state: initial load failed -->
    <section v-else-if="loadError" class="container-wide max-w-3xl py-20 text-center">
      <Icon name="alert-triangle" :size="40" classes="text-danger-500 mx-auto mb-4" />
      <h2 class="font-display text-3xl text-ink-900 mb-3">Couldn't load articles</h2>
      <p class="text-ink-600 text-lg mb-8">Something went wrong reaching the knowledge base. Please try again.</p>
      <button class="btn-primary btn" @click="fetchArticles(true)">
        <Icon name="refresh" :size="14" /> Try again
      </button>
    </section>

    <!-- Empty state: no articles at all -->
    <section v-else-if="articles.length === 0 && !featuredArticle && !loading" class="container-wide max-w-3xl py-20 text-center">
      <div class="text-6xl mb-6">📚</div>
      <h2 class="font-display text-3xl text-ink-900 mb-3">Knowledge base coming soon</h2>
      <p class="text-ink-600 text-lg mb-8">
        Our team is crafting in-depth SEO guides, playbooks, and case studies. Check back shortly — or subscribe below to get notified when we publish.
      </p>
      <router-link to="/" class="btn-primary btn">← Back to Home</router-link>
    </section>

    <template v-else>
      <!-- Search + Category filter -->
      <section v-if="allCategories.length > 1" class="container-wide max-w-6xl py-8 space-y-5">
        <!-- Search bar -->
        <div class="max-w-md mx-auto">
          <form @submit.prevent="onSearchSubmit" class="relative">
            <input
              ref="searchInputRef"
              v-model="searchQuery"
              type="text"
              placeholder="Search articles…"
              class="w-full bg-white border border-brand-400 rounded-full pl-5 pr-24 py-2.5 text-sm text-ink-900 placeholder:text-ink-400 transition-all duration-200 focus:outline-none focus:border-brand-600 focus:ring-4 focus:ring-brand-500/10"
              @input="onSearchInput"
              @keydown.esc="handleSearchInputEscape"
            />
            <button
              v-if="searchQuery"
              type="button"
              @click="clearSearch"
              class="absolute right-12 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-full text-ink-400 hover:text-ink-700 hover:bg-ink-100 transition-colors"
              title="Clear search"
            >
              <Icon name="x" :size="14" />
            </button>
            <button
              type="submit"
              class="absolute right-1.5 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800 transition-colors shadow-soft"
              title="Search"
            >
              <Icon name="arrow-right" :size="16" />
            </button>
          </form>
        </div>
        <!-- Category chips -->
        <div class="flex flex-wrap gap-2 justify-center">
          <button
            @click="setCategory('All')"
            class="chip"
            :class="activeCategory === 'All' ? 'chip-active' : ''"
          >All</button>
          <button
            v-for="cat in allCategories"
            :key="cat.slug"
            @click="setCategory(cat.slug)"
            class="chip"
            :class="activeCategory === cat.slug ? 'chip-active' : ''"
          >
            {{ cat.name }}
          </button>
        </div>
      </section>

      <!-- Featured article (hidden during search) -->
      <section v-if="featuredArticle && !searchQuery" class="container-wide max-w-6xl pb-10">
        <article class="card overflow-hidden lg:grid lg:grid-cols-5 group card-hover">
          <div class="lg:col-span-3 p-8 lg:p-12">
            <div class="flex items-center gap-2 text-2xs font-semibold uppercase tracking-[0.14em]">
              <span class="text-brand-700">Featured</span>
              <span class="text-ink-300">·</span>
              <span class="text-ink-500">{{ featuredArticle.category?.name || 'Guide' }} · {{ featuredArticle.read_time_minutes }} min read</span>
            </div>
            <h2 class="font-display text-3xl lg:text-4xl mt-4 text-ink-900 leading-tight text-balance">
              {{ featuredArticle.title }}
            </h2>
            <p class="mt-4 text-ink-600 leading-relaxed">{{ featuredArticle.excerpt }}</p>
            <div class="mt-6 flex items-center gap-3">
              <router-link :to="`/learn/${featuredArticle.slug}`" class="btn-primary btn-sm">
                Read the guide <Icon name="arrow-right" :size="14" />
              </router-link>
              <div class="flex items-center gap-2 text-xs text-ink-500">
                <span class="w-6 h-6 rounded-full bg-gradient-to-br from-brand-500 to-brand-700 text-white text-2xs font-semibold flex items-center justify-center">LR</span>
                <span>By {{ featuredArticle.author_name || 'The LeadRanks Team' }}</span>
              </div>
            </div>
          </div>
          <div class="lg:col-span-2 bg-gradient-to-br from-brand-50 via-white to-accent-50 p-10 flex items-center justify-center border-l border-ink-100">
            <svg viewBox="0 0 200 200" class="w-full max-w-xs">
              <defs>
                <linearGradient id="ring" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stop-color="#10b981"/>
                  <stop offset="100%" stop-color="#3367ff"/>
                </linearGradient>
              </defs>
              <circle cx="100" cy="100" r="80" fill="none" stroke="#ececea" stroke-width="14"/>
              <circle cx="100" cy="100" r="80" fill="none" stroke="url(#ring)" stroke-width="14" stroke-linecap="round" stroke-dasharray="503" stroke-dashoffset="100" transform="rotate(-90 100 100)"/>
              <text x="100" y="95"  text-anchor="middle" font-family="Instrument Serif, serif" font-size="48" fill="#131210">92</text>
              <text x="100" y="120" text-anchor="middle" font-family="Inter Tight, sans-serif" font-size="11" font-weight="500" fill="#65645f">SCORE</text>
            </svg>
          </div>
        </article>
      </section>

      <!-- Grid of articles -->
      <section class="container-wide max-w-6xl pb-20">
        <!-- Active search badge -->
        <div v-if="searchQuery && !loading" class="mb-6">
          <span class="inline-flex items-center gap-2 text-sm text-ink-600">
            Results for <strong class="text-ink-900">"{{ searchQuery }}"</strong>
            <button @click="clearSearch" class="text-brand-700 hover:underline text-xs">clear</button>
          </span>
        </div>

        <!-- Empty: no results for search -->
        <div v-if="filteredArticles.length === 0 && !loading && searchQuery" class="text-center py-12">
          <p class="text-ink-500 text-lg">No articles matching "{{ searchQuery }}".</p>
          <button @click="clearSearch" class="btn-outline btn-sm mt-4">Clear search</button>
        </div>

        <!-- Empty: no articles in category -->
        <div v-else-if="filteredArticles.length === 0 && !loading" class="text-center py-12">
          <p class="text-ink-500 text-lg">No articles in this category yet.</p>
          <button @click="setCategory('All')" class="btn-outline btn-sm mt-4">Show all articles</button>
        </div>

        <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <article
            v-for="article in filteredArticles"
            :key="article.title"
            class="card card-hover overflow-hidden group cursor-pointer"
          >
            <div class="h-1" :class="categoryColor(article.category?.name)"></div>
            <div class="p-6">
              <div class="flex items-center gap-2 text-2xs text-ink-500 mb-3">
                <span class="font-medium text-ink-700">{{ article.category?.name || 'Guide' }}</span>
                <span class="text-ink-300">·</span>
                <span>{{ article.read_time_minutes }} min</span>
              </div>
              <router-link :to="`/learn/${article.slug}`">
                <h3
                  v-if="searchActive && article.highlighted_title"
                  class="text-lg font-semibold text-ink-900 tracking-tight leading-snug group-hover:text-brand-700 transition-colors"
                  v-html="article.highlighted_title"
                ></h3>
                <h3 v-else class="text-lg font-semibold text-ink-900 tracking-tight leading-snug group-hover:text-brand-700 transition-colors">
                  {{ article.title }}
                </h3>
              </router-link>
              <p
                v-if="searchActive && article.search_snippet"
                class="mt-2 text-sm text-ink-600 leading-relaxed"
                v-html="article.search_snippet"
              ></p>
              <p
                v-else-if="searchActive && article.highlighted_excerpt"
                class="mt-2 text-sm text-ink-600 leading-relaxed"
                v-html="article.highlighted_excerpt"
              ></p>
              <p v-else class="mt-2 text-sm text-ink-600 leading-relaxed">{{ article.excerpt }}</p>
              <router-link :to="`/learn/${article.slug}`" class="mt-4 text-xs font-medium text-brand-700 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                Read more <Icon name="arrow-right" :size="12" />
              </router-link>
            </div>
          </article>
        </div>

        <div class="text-center mt-12">
          <button v-if="hasMore" class="btn-outline btn-lg" @click="loadMore" :disabled="loadingMore">
            {{ loadingMore ? 'Loading…' : 'Load more articles' }}
            <Icon name="chevron-down" :size="14" />
          </button>
          <p v-else-if="!loading && articles.length > 0" class="text-ink-500 text-sm">
            You've seen all {{ articles.length + (featuredArticle ? 1 : 0) }} articles.
          </p>
        </div>
      </section>
    </template>

    <!-- Newsletter -->
    <section class="py-16 bg-ink-900 text-white relative overflow-hidden">
      <div class="absolute inset-0 bg-dot-ink bg-dot-20 opacity-[0.05]"></div>
      <div class="relative container-wide max-w-3xl text-center">
        <div class="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-brand-500 bg-brand-500/10 px-3 py-1 rounded-full border border-brand-500/20 mb-5">
          <Icon name="mail" :size="12" /> Weekly
        </div>
        <h2 class="font-display text-4xl leading-tight text-white">The SEO signal, distilled.</h2>
        <p class="mt-3 text-ink-300">One email a week. New guides, algorithm shifts, and what we're seeing in real audits.</p>

        <form @submit.prevent="subscribe" class="mt-8 flex flex-col sm:flex-row gap-2 max-w-md mx-auto">
          <div class="input-group !bg-white/5 !border-white/20 flex-1">
            <span class="input-group-addon !bg-white/5 !border-white/20 text-ink-500"><Icon name="mail" :size="16" /></span>
            <input v-model="email" type="email" placeholder="you@company.com" class="input !bg-transparent !text-white placeholder:text-ink-500 !border-0 focus:!ring-0" required :disabled="subscribing" />
          </div>
          <button type="submit" class="btn-brand btn" :disabled="subscribing">
            <LoadingSpinner v-if="subscribing" size="sm" color="#fff" track-color="rgba(255,255,255,0.3)" />
            <template v-else>Subscribe <Icon name="arrow-right" :size="14" /></template>
          </button>
        </form>
        <p class="mt-4 text-2xs text-ink-500">No spam. Unsubscribe any time.</p>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { usePageMeta } from '../../composables/usePageMeta.js';
import { markPrerenderReady } from '../../composables/usePrerenderReady';
import { useContentStore } from '../../stores/contentStore';
import Icon from '../ui/Icon.vue';
import LoadingSpinner from '../ui/LoadingSpinner.vue';

const route = useRoute();
const contentStore = useContentStore();

usePageMeta({
  title: 'Learn — LeadRanks',
  meta: [
    { name: 'description', content: 'SEO guides, GEO and AI-search strategy, and technical audits explained in plain language, from the LeadRanks team. Free knowledge base, updated weekly.' },
    { property: 'og:title', content: 'Learn — LeadRanks' },
    { property: 'og:description', content: 'SEO guides, GEO and AI-search strategy, and technical audits explained in plain language, from the LeadRanks team. Free knowledge base, updated weekly.' },
    { property: 'og:image', content: 'https://leadranks.io/og-image-1200x627.png' },
    { name: 'twitter:card', content: 'summary_large_image' },
  ],
});

const activeCategory = ref('All');
const searchQuery = ref('');
const email = ref('');
const searchInputRef = ref(null);
let searchTimer = null;

// ── URL query sync (Task 12b) ──
// replaceState (not pushState) deliberately — search-as-you-type shouldn't
// spam browser history with one entry per keystroke. Still makes refresh,
// direct-link, and copy-paste of a search URL work correctly.
const syncUrl = () => {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams();
  if (searchQuery.value.trim()) params.set('search', searchQuery.value.trim());
  if (activeCategory.value !== 'All') params.set('category', activeCategory.value);
  const qs = params.toString();
  const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
  window.history.replaceState(window.history.state, '', newUrl);
};

// ── Keyboard shortcut (Task 12d) ──
const handleKeydown = (e) => {
  if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
  if (e.key === '/' || (e.key === 'k' && (e.ctrlKey || e.metaKey))) {
    e.preventDefault();
    searchInputRef.value?.focus();
  }
};
const handleSearchInputEscape = () => {
  clearSearch();
  searchInputRef.value?.blur();
};

// Real newsletter signup (2026-09-01) -- this used to be a pure client-side
// fake: a success toast with zero network call, zero storage, no email ever
// sent. Now POSTs to the real, double-opt-in backend (src/api/
// newsletter_endpoints.py) -- the "Check your inbox to confirm" promise is
// now true. Uses window.fetchWithCSRF (the app-wide monkey-patched fetch,
// main.js) rather than a local CSRF reimplementation, same lesson as the
// ProspectingPage.vue/ReportPage.vue fixes earlier this session -- plain
// fetch() already gets correct, auto-refreshing CSRF handling for free.
const subscribing = ref(false);
const subscribe = async () => {
  if (!email.value.trim()) return;
  subscribing.value = true;
  try {
    const resp = await window.fetchWithCSRF('/api/newsletter/subscribe', {
      method: 'POST',
      body: JSON.stringify({ email: email.value.trim(), source: 'learn_page' }),
    });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      const msg = resp.status === 429
        ? (err.detail || 'Too many attempts — please try again in a few minutes.')
        : (err.detail || "Couldn't subscribe. Please try again.");
      window.$toast?.(msg, { type: 'error', title: 'Subscription failed' });
      return;
    }

    const data = await resp.json().catch(() => ({}));
    window.$toast?.(
      data.already_subscribed
        ? "You're already subscribed — no need to confirm again."
        : 'Almost there! Check your inbox to confirm your subscription.',
      { type: 'success', title: data.already_subscribed ? 'Already subscribed' : 'Check your email' },
    );
    email.value = '';
  } catch (e) {
    window.$toast?.('Network error — please try again.', { type: 'error', title: 'Subscription failed' });
  } finally {
    subscribing.value = false;
  }
};

// Lands here after clicking the confirm/unsubscribe link in a newsletter
// email (GET /api/newsletter/confirm|unsubscribe redirects to
// /learn?newsletter=confirmed|unsubscribed|invalid). Shown once, then the
// query param is stripped via replaceState (syncUrl's own established
// technique in this file) so a refresh doesn't re-show it.
const NEWSLETTER_REDIRECT_MESSAGES = {
  confirmed: { text: "You're subscribed! One email a week, starting soon.", type: 'success', title: 'Confirmed' },
  unsubscribed: { text: "You've been unsubscribed. Sorry to see you go.", type: 'info', title: 'Unsubscribed' },
  invalid: { text: 'That link has expired or was already used.', type: 'error', title: 'Link invalid' },
};
const handleNewsletterRedirect = () => {
  const state = typeof route.query.newsletter === 'string' ? route.query.newsletter : null;
  const msg = state && NEWSLETTER_REDIRECT_MESSAGES[state];
  if (!msg) return;
  window.$toast?.(msg.text, { type: msg.type, title: msg.title });
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    params.delete('newsletter');
    const qs = params.toString();
    window.history.replaceState(window.history.state, '', qs ? `${window.location.pathname}?${qs}` : window.location.pathname);
  }
};

// Was keyed to category names ("Fundamentals", "Technical", etc.) that
// don't exist in the real blog_categories table — the actual categories
// are "SEO Guides", "Product Updates", "Case Studies", "Industry News",
// "Find Leads" (confirmed via GET /api/blog/categories), none of which
// matched, so every real article fell through to the grey default. Fixed
// with the real names; default is now brand-500 (matches CampaignsPage.vue's
// accent bar) instead of grey, for any future category added later.
const categoryColor = (cat) => ({
  'Find Leads':       'bg-brand-500',
  'SEO Guides':       'bg-accent-500',
  'Case Studies':     'bg-warn-500',
  'Product Updates':  'bg-ink-900',
  'Industry News':    'bg-danger-500',
}[cat] || 'bg-brand-500');

// ── Fetch real articles from API ──
//
// Seed the initial view from stores/contentStore.js when the router guard
// prefetched for THIS mount (learnFresh). That data is what the SSR snapshot
// was rendered from, so seeding here is what makes the client's first render
// hydration-exact while still showing real content. A query-string view
// (?search=/?category=) deliberately ignores the seed and runs the normal
// fetch path below. See stores/contentStore.js.
const _seed = (!route.query.search && !route.query.category && contentStore.learnFresh)
  ? contentStore.learn
  : null;
const articles = ref(_seed ? _seed.articles : []);
const featuredArticle = ref(_seed ? _seed.featured : null);
const allCategories = ref(_seed ? _seed.categories : []);
const loading = ref(!_seed);
const loadingMore = ref(false);
const page = ref(1);
const totalPages = ref(_seed ? _seed.totalPages : 1);
const hasMore = ref(_seed ? _seed.hasMore : false);
const loadError = ref(_seed ? !!_seed.error : false);

const fetchCategories = async () => {
  try {
    const res = await fetch('/api/blog/categories');
    if (res.ok) {
      const data = await res.json();
      allCategories.value = data.categories || [];
    }
  } catch (e) { /* non-critical */ }
};

const fetchFeatured = async () => {
  try {
    const res = await fetch('/api/blog/articles?featured=true&per_page=1');
    if (res.ok) {
      const data = await res.json();
      if (data.articles?.length > 0) {
        featuredArticle.value = data.articles[0];
      }
    }
  } catch (e) { /* non-critical */ }
};

const onSearchInput = () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    syncUrl();
    fetchArticles(true);
  }, 300);
};

const onSearchSubmit = () => {
  clearTimeout(searchTimer);
  syncUrl();
  fetchArticles(true);
};

const clearSearch = () => {
  searchQuery.value = '';
  syncUrl();
  fetchArticles(true);
};

const fetchArticles = async (reset = false) => {
  if (reset) page.value = 1;
  try {
    const params = new URLSearchParams({ page: page.value, per_page: '12' });
    if (activeCategory.value !== 'All') params.set('category', activeCategory.value);
    if (searchQuery.value.trim()) params.set('search', searchQuery.value.trim());
    const res = await fetch(`/api/blog/articles?${params}`);
    if (!res.ok) throw new Error('Failed');
    const data = await res.json();
    // Exclude featured article from grid only when viewing "All" categories.
    // For specific category views, the featured article IS the category's content
    // and filtering it would show a misleading "No articles in this category yet."
    const articles_ = (featuredArticle.value && activeCategory.value === 'All')
      ? data.articles.filter(a => a.slug !== featuredArticle.value.slug)
      : data.articles;
    if (reset) {
      articles.value = articles_;
    } else {
      articles.value = [...articles.value, ...articles_];
    }
    totalPages.value = data.total_pages;
    hasMore.value = page.value < data.total_pages;
    loadError.value = false;
  } catch (e) {
    console.error('Failed to fetch articles:', e);
    if (reset) loadError.value = true;
  } finally {
    loading.value = false;
    loadingMore.value = false;
  }
};

const setCategory = (cat) => {
  activeCategory.value = cat;
  syncUrl();
  fetchArticles(true);
};

const loadMore = () => {
  page.value++;
  loadingMore.value = true;
  fetchArticles(false);
};

const filteredArticles = computed(() => articles.value);
const searchActive = computed(() => !!searchQuery.value.trim());

// Initial load — restore search/category from the URL first (Task 12b) so a
// direct link to /learn?search=... or a page refresh mid-search loads the
// right results immediately, instead of a blank search that then re-fetches.
onMounted(async () => {
  if (typeof route.query.search === 'string' && route.query.search) {
    searchQuery.value = route.query.search;
  }
  if (typeof route.query.category === 'string' && route.query.category) {
    activeCategory.value = route.query.category;
  }
  handleNewsletterRedirect();
  document.addEventListener('keydown', handleKeydown);

  if (_seed) {
    // Already populated by the router guard for this mount — consume the
    // freshness marker so a later in-app navigation re-fetches, then signal
    // prerender-ready (this is the content-bearing state the snapshot wants).
    contentStore.consumeLearn();
    markPrerenderReady();
    return;
  }

  await Promise.all([fetchCategories(), fetchFeatured()]);
  await fetchArticles(true);
  // Opted out of App.vue's automatic fire (PRERENDER_ASYNC_ROUTES) — real
  // content only exists after these fetches resolve, success or failure.
  markPrerenderReady();
});
onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
});
</script>
